import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import { relationOptions } from '../i18n/relations';
import { useFavicon } from '../utils/useFavicon';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #FAF8F5;
    color: #2C2C2C;
    -webkit-font-smoothing: antialiased;
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .auth-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 24px;
    background: radial-gradient(ellipse 80% 60% at 50% 30%, #FFF0E8 0%, #FAF8F5 70%);
  }
  .auth-logo {
    /* #238 — text wordmark replaced by the full-lockup SVG. */
    display: block;
    text-align: center;
    margin-bottom: 32px;
    text-decoration: none;
    animation: fadeInUp 0.6s ease-out forwards;
  }
  .auth-logo img {
    height: 64px;
    width: auto;
    max-width: 90vw;
    display: inline-block;
  }
  .auth-card {
    background: #fff;
    border-radius: 20px;
    padding: 48px 44px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    animation: fadeInUp 0.6s ease-out 0.1s both;
  }
  .auth-title {
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 32px;
    color: #1A1A1A;
  }
  .form-group { margin-bottom: 20px; }
  .form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #444;
    margin-bottom: 7px;
  }
  .form-input {
    width: 100%;
    padding: 14px 16px;
    font-family: inherit;
    font-size: 1rem;
    border: 1.5px solid #E0DDD8;
    border-radius: 10px;
    background: #FDFCFB;
    color: #2C2C2C;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    -webkit-appearance: none;
  }
  .form-input:focus {
    border-color: #E8845A;
    box-shadow: 0 0 0 3px rgba(232,132,90,0.15);
  }
  .btn-submit {
    width: 100%;
    background: #E8845A;
    color: #fff;
    font-family: inherit;
    font-size: 1rem;
    font-weight: 600;
    padding: 16px;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    margin-top: 8px;
    min-height: 52px;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s;
  }
  .btn-submit:hover:not(:disabled) {
    transform: scale(1.02);
    background: #d9724a;
    box-shadow: 0 6px 20px rgba(232,132,90,0.4);
  }
  .btn-submit:disabled { opacity: 0.65; cursor: not-allowed; }
  .error-msg {
    background: #FFF0EE;
    border: 1px solid #F5C0B4;
    color: #C0392B;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 0.875rem;
    margin-bottom: 20px;
    line-height: 1.5;
  }
  .auth-footer {
    text-align: center;
    margin-top: 28px;
    font-size: 0.875rem;
    color: #666;
  }
  .auth-footer a {
    color: #E8845A;
    font-weight: 600;
    text-decoration: none;
    margin-left: 5px;
    transition: color 0.2s;
  }
  .auth-footer a:hover { color: #d9724a; }
  @media (max-width: 480px) {
    .auth-card { padding: 36px 24px; }
  }
`;

export default function Signup() {
  const { t } = useLang();
  const s = t.signup;
  const navigate = useNavigate();
  const location = useLocation();
  useFavicon('public');  // public-facing surface
  // #741 — perspective is the first thing decided at signup. Two
  // routes feed in: the landing "self-wedge" button passes ?for=self
  // (pre-selects the self path); the main "Create a profile" button
  // arrives without a query string, defaulting to no-choice-made-yet.
  // Either way the user lands on the Choice step first; they can
  // change their mind before continuing to the form.
  const initialPerspective = (() => {
    const q = new URLSearchParams(location.search).get('for');
    return q === 'self' ? 'self' : (q === 'family' ? 'family' : null);
  })();
  const [perspective, setPerspective] = useState(initialPerspective);
  const [step, setStep] = useState(initialPerspective ? 'form' : 'choice');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  // #244-A — captures the family-account holder's relation to the
  // elderly person ("I am Eduard's …"). Required when
  // perspective='family'; hidden + nulled when perspective='self'
  // (the user IS the elderly person, no relation to specify).
  const [relationToElderly, setRelationToElderly] = useState('');
  // #287 — terms acceptance. Required to enable Submit. The terms
  // content shows inline, collapsed by default (no force-expand).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError(s.errors.passwordMismatch);
      return;
    }
    if (!termsAccepted) {
      setError(s.terms.required);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, email, password,
          // #741 — relation_to_elderly is only meaningful for family
          // perspective; null it out for self so the API doesn't store
          // a stray value.
          relation_to_elderly: perspective === 'self' ? null : (relationToElderly || null),
          intake_perspective: perspective || 'family',
          terms_accepted: termsAccepted,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(res.status === 409 ? s.errors.emailExists : s.errors.generic);
      } else {
        localStorage.setItem('am_token', data.token);
        localStorage.setItem('am_user', JSON.stringify(data.user));
        // #222 form-first: new accounts go straight to the profile
        // form (was: dashboard with a "start intake" voice button).
        // The form is the new primary intake surface.
        navigate('/intake/review');
      }
    } catch {
      setError(s.errors.generic);
    } finally {
      setLoading(false);
    }
  }

  // #741 — step-1 choice screen. Renders before the email/password
  // form when no `?for=...` query string was supplied. Picking either
  // option sets `perspective` and advances to the form; the user can
  // come back to swap with the "← Change" link.
  if (step === 'choice') {
    return (
      <>
        <style>{styles}</style>
        <div className="auth-page">
          <Link to="/" className="auth-logo" aria-label="Amigo Mio home">
            <img src="/logo/logo-full-watercolor.png" alt="Amigo Mio — AI Voice Companion" />
          </Link>
          <LanguageSwitcher />
          <div className="auth-card">
            <h1 className="auth-title">{s.choice?.title || 'Who is this for?'}</h1>
            <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: 24, marginTop: -16 }}>
              {s.choice?.subtitle || 'Pick the one that fits. You can change your mind on the next screen.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                type="button"
                className="btn-submit"
                onClick={() => { setPerspective('family'); setStep('form'); }}
                style={{ margin: 0, padding: '20px 16px', fontSize: '1.05rem', lineHeight: 1.3, height: 'auto' }}
              >
                {s.choice?.forLoved || '👵 For a loved one'}
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={() => { setPerspective('self'); setStep('form'); }}
                style={{ margin: 0, padding: '20px 16px', fontSize: '1.05rem', lineHeight: 1.3, height: 'auto', background: '#fff', color: '#E8845A', border: '2px solid #E8845A' }}
              >
                {s.choice?.forSelf || '🧓 For myself'}
              </button>
            </div>
            <div style={{ marginTop: 28, textAlign: 'center', fontSize: '0.9rem' }}>
              {s.haveAccount} <Link to="/login" style={{ color: '#E8845A', fontWeight: 500 }}>{s.signIn}</Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="auth-page">
        <Link to="/" className="auth-logo" aria-label="Amigo Mio home">
          <img src="/logo/logo-full-watercolor.png" alt="Amigo Mio — AI Voice Companion" />
        </Link>
        <LanguageSwitcher />
        <div className="auth-card">
          {/* #741 — "← Change" lets the user re-pick perspective
              without losing already-typed email/password (state is
              kept across the step swap). Hidden when the user arrived
              with ?for=... pre-set from the landing wedge — they made
              that choice already; offering to change it is noise. */}
          {!initialPerspective && (
            <button
              type="button"
              onClick={() => setStep('choice')}
              style={{ background: 'none', border: 'none', color: '#E8845A', cursor: 'pointer', padding: 0, fontSize: '0.88rem', fontWeight: 500, marginBottom: 16 }}
            >{s.change}</button>
          )}
          <h1 className="auth-title">{s.title}</h1>
          {error && <div className="error-msg">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">{s.name}</label>
              <input
                className="form-input"
                type="text"
                required
                autoComplete="name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{s.email}</label>
              <input
                className="form-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{s.password}</label>
              <input
                className="form-input"
                type="password"
                required
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{s.confirmPassword}</label>
              <input
                className="form-input"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
              />
            </div>
            {/* #244-A — relation to the elderly person. Required at
                signup so notification copy + Berta context have the
                anchor ("Your father's reminder…" instead of "the user's
                reminder…").
                #741 — only shown / required for family perspective.
                Self-perspective accounts skip this field entirely. */}
            {perspective !== 'self' && (
            <div className="form-group">
              <label className="form-label">{s.relationLabel}</label>
              <select
                className="form-input"
                required
                value={relationToElderly}
                onChange={e => setRelationToElderly(e.target.value)}
              >
                <option value="">{s.relationPlaceholder}</option>
                {/* #1107 — single source of truth for the relation
                    vocabulary (web/src/i18n/relations.js), labels
                    translated via t.relations. Was a hardcoded English
                    list duplicated across four files; now
                    Boyfriend/Girlfriend/Partner land everywhere at once
                    and follow the selected language. */}
                {relationOptions(t).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            )}

            {/* #287 — terms acceptance. Required to submit; content
                shown inline-but-collapsed (no force-expand). */}
            <div className="form-group" style={{ background: '#FDFCFB', border: '1.5px solid #E0DDD8', borderRadius: 10, padding: 14 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: '0.95rem', color: '#2C2C2C', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  required
                  style={{ marginTop: 3, flexShrink: 0 }}
                />
                <span>
                  {s.terms.agree} <button
                    type="button"
                    onClick={() => setTermsExpanded(v => !v)}
                    style={{ background: 'none', border: 'none', color: '#E8845A', cursor: 'pointer', padding: 0, fontWeight: 600, fontSize: 'inherit' }}
                  >{s.terms.link} {termsExpanded ? s.terms.hide : s.terms.read}</button>.
                </span>
              </label>
              {termsExpanded && (
                <div style={{ marginTop: 12, padding: 12, background: '#fff', borderRadius: 8, fontSize: '0.85rem', color: '#444', lineHeight: 1.55 }}>
                  {/* PLACEHOLDER — replace with the real terms text before
                      shipping. Bas owns the wording (see #287). */}
                  <p style={{ marginBottom: 8 }}><strong>What AmigoMio is</strong></p>
                  <p style={{ marginBottom: 8 }}>AmigoMio is an AI companion app for elderly people. It is a help, not a guarantee — and it is <strong>not</strong> a medical or emergency service.</p>
                  <p style={{ marginBottom: 8 }}><strong>Notifications and alerts</strong></p>
                  <p style={{ marginBottom: 8 }}>Reminders, missed-call notifications, and wellbeing alerts are best-effort. Do not rely on them for time-critical or medical events. AmigoMio cannot guarantee delivery, timing, or that an alert will reach you.</p>
                  <p style={{ marginBottom: 8 }}><strong>Data</strong></p>
                  <p style={{ marginBottom: 8 }}>We process and store conversation data, voice metadata, and the information you provide to deliver the service. Hosting is in the EU.</p>
                  <p style={{ marginBottom: 0, fontStyle: 'italic', color: '#888' }}>
                    Note: this text is a placeholder. The full terms will be replaced with the legally-reviewed version before public launch.
                  </p>
                </div>
              )}
            </div>

            <button className="btn-submit" type="submit" disabled={loading || !termsAccepted}>
              {loading ? '…' : s.submit}
            </button>
          </form>
          <p className="auth-footer">
            {s.haveAccount}
            <Link to="/login">{s.signIn}</Link>
          </p>
        </div>
      </div>
    </>
  );
}
